import { useFetcher, useSubmit } from "@remix-run/react";
import React from "react";
import type { signInAction } from "./.server/actions";
import type { authTeamLoader } from "./.server/loaders";
import type { SignInSchema, SignOutSchema, SignUpInviteSchema, UserProfileSchema } from "./schema";

/**
 * Sign in, sign up and sign out a user.
 */
export function useAuthAction() {
  const submit = useSubmit();

  return {
    signInWithEmail: (opts: SignInSchema) => {
      submit(opts, { method: "post", action: "/sign-in", navigate: false });
    },
    signIn: (() => {
      const { submit: fetcherSubmit, ...fetcher } = useFetcher<typeof signInAction>({
        key: "auth:sign-in",
      });
      const isLoading = React.useMemo(() => fetcher.state !== "idle", [fetcher.state]);
      const isError = React.useMemo(() => fetcher.data?.error, [fetcher.data, fetcher.state]);
      const data = React.useMemo(() => fetcher.data, [fetcher.data, fetcher.state]);

      return {
        isLoading,
        isError,
        data,
        submit: (opts: SignInSchema) => {
          const formData = new FormData();

          for (const [key, value] of Object.entries(opts)) {
            if (value !== undefined) formData.set(key, value);
          }

          fetcherSubmit(formData, {
            method: "post",
            action: "/sign-in",
          });
        },
      };
    })(),
    signOut: (opts: SignOutSchema) =>
      submit(opts, { method: "post", action: "/sign-out?redirectTo=/", navigate: false }),
    updateUserProfile: (opts: UserProfileSchema) => {
      const formData = new FormData();

      for (const [key, value] of Object.entries(opts)) {
        if (value !== undefined) formData.set(key, value);
      }

      submit(formData, { method: "post", action: "/profile", navigate: false });
    },
    inviteTeam: (opts: SignUpInviteSchema) => {
      const formData = new FormData();

      for (const [key, value] of Object.entries(opts)) formData.set(key, value);

      submit(formData, { method: "post", action: "/team", navigate: false });
    },
  };
}

export function useAuthLoader() {
  return {
    team: (() => {
      const fetcher = useFetcher<typeof authTeamLoader>({ key: "auth:team" });

      return {
        ...fetcher,
        load: (search?: string) => fetcher.load(`/team${search ? `?search=${search}` : ""}`),
      };
    })(),
  };
}
